import React, { useEffect } from "react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Card, CardContent } from "../ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Checkbox } from "../ui/checkbox";
import { Info } from "lucide-react";
import {
  getCountries,
  getCities,
  getDesignConditions,
} from "../../data/ashraeDesignConditions";
import { unitLabel } from "../../lib/units";
import { isModelN, isPrimaryAir, isModelO2, getProjectFamilies } from "../../lib/wizardCalc";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import NumberInput from "./NumberInput";

// B.1.7 — DesignConditionsTab is a single compressed card.
// Density target: fits on one 1440px screen without scrolling.
// Sections:
//   1. Outdoor design   (Model N projects only)
//   2. Room conditions
//   3. Water conditions (EWT cool, EWT heat, water flow, glycol)
//   4. Air-on overrides
//   5. Primary-Air conditions (PA induction models only)
//   6. Model-specific (O-2 circuit, Model N settings, PA static pressure)
export default function DesignConditionsTab({
  project,
  updateProject,
  rooms = [],
  isSI = false,
  onMaxLengthChange,
  hideLengthInputs = false,
  paFlowTestId = "design-pa-flow-cfm",
}) {
  const dc = project.designConditions || {};
  const advanced = project.advanced || {};
  const countries = getCountries();
  const cities = dc.country && dc.country !== "__other__" ? getCities(dc.country) : [];

  const needsOutdoor = (rooms || []).some((r) => isModelN(r.unit_model));
  const needsPrimaryAir = (rooms || []).some((r) => isPrimaryAir(r.unit_model));

  const isOtherCountry = dc.country === "__other__";
  const ashraeApplied = !!(
    dc.country &&
    dc.country !== "__other__" &&
    dc.city &&
    dc.city !== "__manual__" &&
    getDesignConditions(dc.country, dc.city)
  );

  // Model-family detection for conditional model-specific sections
  const { hasFACB, hasACB, hasEWS } = getProjectFamilies(rooms);
  const models = [...new Set((rooms || []).map((r) => r.unit_model).filter(Boolean))];
  const hasO2 = models.some(isModelO2);
  const hasN = models.some(isModelN);
  const hasPA = (rooms || []).some((r) => isPrimaryAir(r.unit_model));

  // FACB strategy needed to display the dynamic water-flow label
  const facbStrategy = project.facb_strategy || "lowest_speed";
  const isGivenFlow = facbStrategy === "given_flow";

  const setDC = (key, val) =>
    updateProject({ ...project, designConditions: { ...dc, [key]: val } });
  const setAdv = (key, val) =>
    updateProject({ ...project, advanced: { ...advanced, [key]: val } });

  // Auto-populate ASHRAE conditions when city is selected.
  useEffect(() => {
    if (dc.country && dc.city && dc.city !== "__manual__") {
      const c = getDesignConditions(dc.country, dc.city);
      if (c) {
        updateProject({
          ...project,
          designConditions: {
            ...dc,
            outdoor_db_f: c.summer_db,
            outdoor_wb_f: c.summer_wb,
            outdoor_db_winter_f: c.winter_db,
            altitude_ft: c.elevation_ft ?? c.altitude_ft ?? 0,
          },
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dc.country, dc.city]);

  return (
    <Card data-testid="wizard-design-tab">
      <CardContent className="px-4 pt-4 pb-4 space-y-0">

        {/* ------------------------------------------------------------------ */}
        {/* 1. Outdoor design — Model N projects only                           */}
        {/* ------------------------------------------------------------------ */}
        {needsOutdoor && (
          <div className="border-b border-border pb-3 mb-3">
            <div className="flex items-center gap-2 mb-2">
              <p className="text-xs font-semibold text-foreground">Outdoor Design</p>
              {ashraeApplied && (
                <span
                  className="inline-flex items-center rounded border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-800"
                  data-testid="wizard-dc-ashrae-badge"
                  title="Values auto-populated from ASHRAE Climatic Design Conditions for the selected city"
                >
                  ASHRAE · 0.4% / 99.6%
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {/* Location pickers */}
              <div>
                <Label className="text-xs font-medium mb-1 block">Country</Label>
                <Select
                  value={dc.country || ""}
                  onValueChange={(v) =>
                    updateProject({
                      ...project,
                      designConditions: {
                        ...dc,
                        country: v,
                        city: v !== dc.country ? "" : dc.city,
                      },
                    })
                  }
                >
                  <SelectTrigger className="h-8 text-xs" data-testid="wizard-dc-country">
                    <SelectValue placeholder="Country" />
                  </SelectTrigger>
                  <SelectContent>
                    {countries.map((c) => (
                      <SelectItem key={c} value={c} className="text-xs">
                        {c}
                      </SelectItem>
                    ))}
                    <SelectItem value="__other__" className="text-xs">
                      Other (manual entry)
                    </SelectItem>
                  </SelectContent>
                </Select>
                {isOtherCountry && (
                  <Input
                    type="text"
                    className="mt-1 h-8 text-xs"
                    placeholder="Country name"
                    value={dc.country_other || ""}
                    onChange={(e) => setDC("country_other", e.target.value)}
                    data-testid="wizard-dc-country-other"
                  />
                )}
              </div>
              <div>
                <Label className="text-xs font-medium mb-1 block">City</Label>
                {isOtherCountry ? (
                  <Input
                    type="text"
                    className="h-8 text-xs"
                    placeholder="City name"
                    value={dc.city_other || ""}
                    onChange={(e) => setDC("city_other", e.target.value)}
                    data-testid="wizard-dc-city-other"
                  />
                ) : (
                  <Select
                    value={dc.city || ""}
                    onValueChange={(v) => setDC("city", v)}
                    disabled={!dc.country}
                  >
                    <SelectTrigger className="h-8 text-xs" data-testid="wizard-dc-city">
                      <SelectValue placeholder="City" />
                    </SelectTrigger>
                    <SelectContent>
                      {cities.map((c) => (
                        <SelectItem key={c} value={c} className="text-xs">
                          {c}
                        </SelectItem>
                      ))}
                      <SelectItem value="__manual__" className="text-xs">
                        Manual entry
                      </SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
              {/* Outdoor DB/WB + winter DB + altitude */}
              <div>
                <Label className="text-xs font-medium mb-1 block">
                  Summer DB ({unitLabel("temp", isSI)})
                </Label>
                <NumberInput
                  value={dc.outdoor_db_f}
                  onChange={(v) => setDC("outdoor_db_f", v)}
                  isSI={isSI}
                  kind="temp"
                  className="h-8 text-xs font-mono tabular-nums"
                  data-testid="wizard-dc-outdoor-db"
                />
              </div>
              <div>
                <Label className="text-xs font-medium mb-1 block">
                  Summer WB ({unitLabel("temp", isSI)})
                </Label>
                <NumberInput
                  value={dc.outdoor_wb_f}
                  onChange={(v) => setDC("outdoor_wb_f", v)}
                  isSI={isSI}
                  kind="temp"
                  className="h-8 text-xs font-mono tabular-nums"
                  data-testid="wizard-dc-outdoor-wb"
                />
              </div>
              <div>
                <Label className="text-xs font-medium mb-1 block">
                  Winter DB ({unitLabel("temp", isSI)})
                </Label>
                <NumberInput
                  value={dc.outdoor_db_winter_f}
                  onChange={(v) => setDC("outdoor_db_winter_f", v)}
                  isSI={isSI}
                  kind="temp"
                  className="h-8 text-xs font-mono tabular-nums"
                  data-testid="wizard-dc-outdoor-winter"
                />
              </div>
              <div>
                <Label className="text-xs font-medium mb-1 block">
                  Altitude ({isSI ? "m" : "ft"})
                </Label>
                <NumberInput
                  value={dc.altitude_ft}
                  onChange={(v) => setDC("altitude_ft", v)}
                  isSI={isSI}
                  kind="altitude"
                  decimals={0}
                  className="h-8 text-xs font-mono tabular-nums"
                  data-testid="wizard-dc-altitude"
                />
              </div>
            </div>
          </div>
        )}

        {/* ------------------------------------------------------------------ */}
        {/* 2. Room conditions                                                  */}
        {/* ------------------------------------------------------------------ */}
        <div className="border-b border-border pb-3 mb-3">
          <p className="text-xs font-semibold text-foreground mb-2">Room Conditions</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs font-medium mb-1 block">
                Summer DB ({unitLabel("temp", isSI)})
              </Label>
              <NumberInput
                value={dc.room_db_f}
                onChange={(v) => setDC("room_db_f", v)}
                isSI={isSI}
                kind="temp"
                className="h-8 text-xs font-mono tabular-nums"
                data-testid="wizard-dc-room-db"
              />
            </div>
            <div>
              <Label className="text-xs font-medium mb-1 block">Summer RH (%)</Label>
              <Input
                type="number"
                className="h-8 text-xs font-mono tabular-nums"
                value={dc.room_rh || ""}
                onChange={(e) => setDC("room_rh", e.target.value)}
                data-testid="wizard-dc-room-rh"
              />
            </div>
            <div>
              <Label className="text-xs font-medium mb-1 block">
                Winter DB ({unitLabel("temp", isSI)})
              </Label>
              <NumberInput
                value={dc.room_db_winter_f}
                onChange={(v) => setDC("room_db_winter_f", v)}
                isSI={isSI}
                kind="temp"
                className="h-8 text-xs font-mono tabular-nums"
              />
            </div>
            <div>
              <Label className="text-xs font-medium mb-1 block">Winter RH (%)</Label>
              <Input
                type="number"
                className="h-8 text-xs font-mono tabular-nums"
                value={dc.room_rh_winter || ""}
                onChange={(e) => setDC("room_rh_winter", e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* ------------------------------------------------------------------ */}
        {/* 3. Water conditions                                                 */}
        {/* ------------------------------------------------------------------ */}
        <div className="border-b border-border pb-3 mb-3">
          <p className="text-xs font-semibold text-foreground mb-2">Water Conditions</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs font-medium mb-1 block">
                EWT Cooling ({unitLabel("temp", isSI)})
              </Label>
              <NumberInput
                value={dc.water_entering_f}
                onChange={(v) => setDC("water_entering_f", v)}
                isSI={isSI}
                kind="temp"
                className="h-8 text-xs font-mono tabular-nums"
                data-testid="wizard-dc-water-cool"
              />
            </div>
            <div>
              <Label className="text-xs font-medium mb-1 block">
                EWT Heating ({unitLabel("temp", isSI)})
              </Label>
              <NumberInput
                value={dc.water_entering_winter_f}
                onChange={(v) => setDC("water_entering_winter_f", v)}
                isSI={isSI}
                kind="temp"
                className="h-8 text-xs font-mono tabular-nums"
                data-testid="wizard-dc-water-heat"
              />
            </div>

            {/* Max / Fixed water flow — visible for FACB projects only.
                Label toggles live based on facb_strategy. */}
            {hasFACB && (
              <div>
                <Label
                  className="text-xs font-medium mb-1 block"
                  htmlFor="dc-water-flow-input"
                >
                  {isGivenFlow
                    ? `Fixed flow (${unitLabel("waterFlow", isSI)})`
                    : `Max flow (${unitLabel("waterFlow", isSI)})`}
                </Label>
                <NumberInput
                  id="dc-water-flow-input"
                  value={advanced.water_flow_gpm}
                  onChange={(v) => setAdv("water_flow_gpm", v)}
                  isSI={isSI}
                  kind="waterFlow"
                  decimals={isSI ? 3 : 2}
                  placeholder="Auto"
                  className="h-8 text-xs font-mono tabular-nums"
                  data-testid="wizard-dc-water-flow"
                />
                <p className="text-[10px] text-muted-foreground mt-0.5">
                  {isGivenFlow
                    ? "FACB strategy B — engine holds this flow."
                    : "FACB strategy A — upper bound for water bisection."}
                </p>
              </div>
            )}

            {/* Glycol toggle + type + % */}
            <div className="flex items-center gap-2 self-end pb-1">
              <Checkbox
                id="use-glycol"
                checked={!!advanced.use_glycol}
                onCheckedChange={(v) => setAdv("use_glycol", !!v)}
                data-testid="wizard-dc-use-glycol"
              />
              <Label htmlFor="use-glycol" className="text-xs font-medium cursor-pointer">
                Glycol
              </Label>
            </div>
            {advanced.use_glycol && (
              <>
                <div>
                  <Label className="text-xs font-medium mb-1 block">Glycol Type</Label>
                  <Select
                    value={advanced.glycol_type || "PG"}
                    onValueChange={(v) => setAdv("glycol_type", v)}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PG" className="text-xs">Propylene</SelectItem>
                      <SelectItem value="EG" className="text-xs">Ethylene</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-medium mb-1 block">Glycol %</Label>
                  <Input
                    type="number"
                    className="h-8 text-xs font-mono tabular-nums"
                    placeholder="%"
                    value={advanced.glycol_percentage || ""}
                    onChange={(e) => setAdv("glycol_percentage", e.target.value)}
                  />
                </div>
              </>
            )}
          </div>
        </div>

        {/* ------------------------------------------------------------------ */}
        {/* 4. Air-On temperature overrides                                     */}
        {/* ------------------------------------------------------------------ */}
        <div className="border-b border-border pb-3 mb-3">
          <p className="text-xs font-semibold text-foreground mb-1">
            Air-On Temperature{" "}
            <span className="font-normal text-muted-foreground">(optional overrides)</span>
          </p>
          <p className="text-[10px] text-muted-foreground mb-2">
            Overrides the default 2°F induction offset. Leave blank to use room DB.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs font-medium mb-1 block">
                Cooling ({unitLabel("temp", isSI)})
              </Label>
              <NumberInput
                value={advanced.air_on_temp_f}
                onChange={(v) => setAdv("air_on_temp_f", v)}
                isSI={isSI}
                kind="temp"
                placeholder="Room DB"
                className="h-8 text-xs font-mono tabular-nums"
                data-testid="wizard-adv-airon-cool"
              />
            </div>
            <div>
              <Label className="text-xs font-medium mb-1 block">
                Heating ({unitLabel("temp", isSI)})
              </Label>
              <NumberInput
                value={advanced.air_on_temp_heating_f}
                onChange={(v) => setAdv("air_on_temp_heating_f", v)}
                isSI={isSI}
                kind="temp"
                placeholder="Room DB"
                className="h-8 text-xs font-mono tabular-nums"
                data-testid="wizard-adv-airon-heat"
              />
            </div>
          </div>
        </div>

        {/* ------------------------------------------------------------------ */}
        {/* 5. Primary-Air conditions (PA induction models only)                */}
        {/* ------------------------------------------------------------------ */}
        {needsPrimaryAir && (
          <div className="border-b border-border pb-3 mb-3">
            <p className="text-xs font-semibold text-foreground mb-2">
              Primary-Air Conditions (Models I / L / C / C1 / H)
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <Label className="text-xs font-medium mb-1 block">
                  PA DB ({unitLabel("temp", isSI)})
                </Label>
                <NumberInput
                  placeholder="Outdoor DB"
                  value={dc.pa_eat_db_f}
                  onChange={(v) => setDC("pa_eat_db_f", v)}
                  isSI={isSI}
                  kind="temp"
                  className="h-8 text-xs font-mono tabular-nums"
                  data-testid="wizard-dc-pa-db"
                />
              </div>
              <div>
                <Label className="text-xs font-medium mb-1 block">PA RH (%)</Label>
                <Input
                  type="number"
                  className="h-8 text-xs font-mono tabular-nums"
                  placeholder="Outdoor RH"
                  value={dc.pa_eat_rh || ""}
                  onChange={(e) => setDC("pa_eat_rh", e.target.value)}
                  data-testid="wizard-dc-pa-rh"
                />
              </div>
              <div>
                <Label className="text-xs font-medium mb-1 block">
                  PA DB Winter ({unitLabel("temp", isSI)})
                </Label>
                <NumberInput
                  placeholder="Summer PA DB"
                  value={dc.pa_eat_db_winter_f}
                  onChange={(v) => setDC("pa_eat_db_winter_f", v)}
                  isSI={isSI}
                  kind="temp"
                  className="h-8 text-xs font-mono tabular-nums"
                  data-testid="wizard-dc-pa-db-winter"
                />
              </div>
              <div>
                <Label className="text-xs font-medium mb-1 block">PA RH Winter (%)</Label>
                <Input
                  type="number"
                  className="h-8 text-xs font-mono tabular-nums"
                  placeholder="Summer PA RH"
                  value={dc.pa_eat_rh_winter || ""}
                  onChange={(e) => setDC("pa_eat_rh_winter", e.target.value)}
                  data-testid="wizard-dc-pa-rh-winter"
                />
              </div>
              <div>
                <TooltipProvider delayDuration={150}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Label
                        htmlFor={paFlowTestId}
                        className="flex items-center gap-1 cursor-default w-fit text-xs font-medium mb-1"
                      >
                        PA Flow ({unitLabel("flow", isSI)})
                        <Info className="h-3 w-3 text-muted-foreground" aria-hidden />
                      </Label>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs text-xs">
                      Primary air flow rate per unit. Required for L, I, C, C1, and H model families.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <NumberInput
                  id={paFlowTestId}
                  placeholder="Room OA"
                  value={advanced.pa_flow_cfm}
                  onChange={(v) => setAdv("pa_flow_cfm", v)}
                  isSI={isSI}
                  kind="flow"
                  decimals={isSI ? 1 : 0}
                  className="h-8 text-xs font-mono tabular-nums"
                  data-testid={paFlowTestId}
                />
              </div>
            </div>
          </div>
        )}

        {/* ------------------------------------------------------------------ */}
        {/* 6. Model-specific — shown conditionally on family presence           */}
        {/* ------------------------------------------------------------------ */}
        {(hasO2 || hasN || hasPA) && (
          <div>
            <p className="text-xs font-semibold text-foreground mb-2">Model-Specific</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">

              {/* O-2 circuit arrangement */}
              {hasO2 && (
                <div>
                  <Label className="text-xs font-medium mb-1 block">
                    O-2 Circuit
                  </Label>
                  <Select
                    value={advanced.o2_circuit || "serial"}
                    onValueChange={(v) => setAdv("o2_circuit", v)}
                  >
                    <SelectTrigger className="h-8 text-xs" data-testid="wizard-adv-o2-circuit">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="serial" className="text-xs">
                        Serial (Op then ACB)
                      </SelectItem>
                      <SelectItem value="parallel" className="text-xs">
                        Parallel
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    Default: serial
                  </p>
                </div>
              )}

              {/* Model N recovery type */}
              {hasN && (
                <div>
                  <Label className="text-xs font-medium mb-1 block">
                    N Recovery Type
                  </Label>
                  <Select
                    value={advanced.recovery_type || "HRV"}
                    onValueChange={(v) => setAdv("recovery_type", v)}
                  >
                    <SelectTrigger className="h-8 text-xs" data-testid="wizard-adv-recovery">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="HRV" className="text-xs">
                        HRV (sensible only)
                      </SelectItem>
                      <SelectItem value="ERV" className="text-xs">
                        ERV (sens + latent)
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Model N non-condensing mode */}
              {hasN && (
                <div className="flex items-center gap-2 self-end pb-1">
                  <Checkbox
                    id="non-cond"
                    checked={!!advanced.non_condensing_mode}
                    onCheckedChange={(v) => setAdv("non_condensing_mode", !!v)}
                    data-testid="wizard-adv-noncond"
                  />
                  <Label htmlFor="non-cond" className="text-xs font-medium cursor-pointer">
                    Spinnaker Non-Condensing
                  </Label>
                </div>
              )}

              {/* PA static pressure — shown for ACB/PA induction models */}
              {hasPA && (
                <div>
                  <Label className="text-xs font-medium mb-1 block">
                    PA Static Pressure ({unitLabel("staticPressure", isSI)})
                  </Label>
                  <NumberInput
                    placeholder="Default"
                    value={advanced.pa_pressure_in_wg}
                    onChange={(v) => setAdv("pa_pressure_in_wg", v)}
                    isSI={isSI}
                    kind="staticPressure"
                    decimals={isSI ? 0 : 2}
                    className="h-8 text-xs font-mono tabular-nums"
                    data-testid="wizard-adv-pa-static"
                  />
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    PA flow uses OA from each room.
                  </p>
                </div>
              )}

            </div>
          </div>
        )}

      </CardContent>
    </Card>
  );
}
